import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRIORITIES = [
  { value: 'low', label: 'Basse' },
  { value: 'medium', label: 'Normale' },
  { value: 'high', label: 'Haute' },
];

/**
 * Modal réutilisable de création d'une tâche.
 * Props :
 *   - defaultLinkedPropertyId : pré-renseigne linked_property_id (fiche bien)
 *   - defaultLinkedContactId  : pré-renseigne linked_contact_id (fiche contact)
 *   - defaultTitle : titre suggéré
 *   - onClose() / onCreated(task)
 */
const TaskCreateModal = ({
  defaultLinkedPropertyId = null,
  defaultLinkedContactId = null,
  defaultTitle = '',
  onClose,
  onCreated,
}) => {
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({
    title: defaultTitle,
    description: '',
    due_date: '',
    priority: 'medium',
    assignment_mode: 'all',
    assigned_to_user_ids: [],
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/users/negotiators`);
        if (!cancelled) setUsers(res.data || []);
      } catch {
        if (!cancelled) setUsers([]);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const toggleUser = (uid) => {
    setForm((f) => ({
      ...f,
      assigned_to_user_ids: f.assigned_to_user_ids.includes(uid)
        ? f.assigned_to_user_ids.filter((x) => x !== uid)
        : [...f.assigned_to_user_ids, uid],
    }));
  };

  const handleSubmit = async () => {
    if (!form.title.trim()) {
      toast.error('Le titre est obligatoire');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        title: form.title.trim(),
        description: form.description || null,
        due_date: form.due_date ? new Date(form.due_date).toISOString() : null,
        priority: form.priority,
        assigned_to_user_ids: form.assignment_mode === 'all' ? [] : form.assigned_to_user_ids,
        linked_property_id: defaultLinkedPropertyId || null,
        linked_contact_id: defaultLinkedContactId || null,
      };
      const res = await axios.post(`${API}/tasks`, payload);
      toast.success('Tâche créée');
      window.dispatchEvent(new Event('tasks-changed'));
      onCreated?.(res.data);
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur lors de la création');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-[60] p-4"
      onClick={onClose}
      data-testid="task-create-modal"
    >
      <div
        className="bg-white rounded-xl shadow-2xl max-w-xl w-full max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
              <i className="bi bi-check2-square text-blue-600 text-lg"></i>
            </div>
            <h3 className="text-base font-bold text-slate-800">Nouvelle tâche</h3>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="task-create-modal-close">
            <i className="bi bi-x-lg text-lg"></i>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {(defaultLinkedPropertyId || defaultLinkedContactId) && (
            <div className="text-xs text-blue-700 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
              <i className="bi bi-link-45deg mr-1"></i>
              {defaultLinkedPropertyId ? 'Cette tâche sera liée à ce bien.' : 'Cette tâche sera liée à ce contact.'}
            </div>
          )}

          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">
              Titre <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="Ex : Relancer pour le mandat"
              className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              data-testid="task-create-title"
              autoFocus
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">Échéance</label>
              <input
                type="date"
                value={form.due_date}
                onChange={(e) => setForm({ ...form, due_date: e.target.value })}
                className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                data-testid="task-create-due-date"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">Priorité</label>
              <select
                value={form.priority}
                onChange={(e) => setForm({ ...form, priority: e.target.value })}
                className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                data-testid="task-create-priority"
              >
                {PRIORITIES.map((p) => (<option key={p.value} value={p.value}>{p.label}</option>))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-2">Assignation</label>
            <div className="flex flex-col sm:flex-row gap-2 mb-2">
              <label className="flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-300 cursor-pointer hover:bg-slate-50 flex-1">
                <input
                  type="radio"
                  name="assignment_mode"
                  checked={form.assignment_mode === 'all'}
                  onChange={() => setForm({ ...form, assignment_mode: 'all', assigned_to_user_ids: [] })}
                />
                <span className="text-sm">Toute l'agence</span>
              </label>
              <label className="flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-300 cursor-pointer hover:bg-slate-50 flex-1">
                <input
                  type="radio"
                  name="assignment_mode"
                  checked={form.assignment_mode === 'specific'}
                  onChange={() => setForm({ ...form, assignment_mode: 'specific' })}
                />
                <span className="text-sm">Utilisateur(s) spécifique(s)</span>
              </label>
            </div>
            {form.assignment_mode === 'specific' && (
              <div className="border border-slate-200 rounded-lg p-2 max-h-32 overflow-y-auto bg-slate-50">
                {users.length === 0 ? (
                  <p className="text-xs text-slate-500 text-center py-2">Chargement…</p>
                ) : (
                  users.map((u) => (
                    <label key={u.id} className="flex items-center gap-2 py-1 px-2 hover:bg-white rounded cursor-pointer">
                      <input
                        type="checkbox"
                        checked={form.assigned_to_user_ids.includes(u.id)}
                        onChange={() => toggleUser(u.id)}
                      />
                      <span className="text-sm">{u.prenom} {u.nom} <span className="text-xs text-slate-500">· {u.email}</span></span>
                    </label>
                  ))
                )}
              </div>
            )}
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">Description (optionnel)</label>
            <textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              rows={3}
              placeholder="Notes, contexte…"
              className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 resize-none"
              data-testid="task-create-description"
            />
          </div>
        </div>

        <div className="px-5 py-3 border-t border-slate-200 flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 rounded-lg"
          >
            Annuler
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting || !form.title.trim()}
            className="px-4 py-2 text-sm font-semibold bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 text-white rounded-lg flex items-center gap-2"
            data-testid="task-create-submit"
          >
            <i className="bi bi-check-lg"></i>
            {submitting ? 'Création…' : 'Créer la tâche'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TaskCreateModal;
